import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

const LEFT_PAGE = "LEFT";
const RIGHT_PAGE = "RIGHT";

function Pagination(props) {
  const [totalRecords, setTotalRecords] = useState('');
  const [pageNeighbours, setPageNeighbours] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pages, setPages] = useState('');

    useEffect(() => {
        gotoPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalPages]);

    useEffect(() => {
        if(typeof props.pageNeighbours === "number") {
            setPageNeighbours(Math.max(0, Math.min(props.pageNeighbours, 2)));
        }
    }, [props.pageNeighbours])

    useEffect(() => {
        // Has to be done until all components use pagination wrapper
        let totalRecords = props.totalRecords;
        if(isNaN(props.totalRecords)) {
            totalRecords = props.totalRecords.length;
        }
        
        let pageLimit = 30;
        if(props.pageLimit) {
            pageLimit = props.pageLimit;
        }

        let totalPages = Math.ceil(totalRecords / pageLimit);
        
        if(currentPage > 0 && totalPages > 0 && totalRecords) {
            const paginationData = {
                currentPage,
                totalPages,
                pageLimit,
                totalRecords
            };
            
            props.onPageChanged(paginationData);
            if(props.updateParent) {
                props.updateParent({ currentPage, totalPages });
            }
        }
        setTotalPages(totalPages);
        setTotalRecords(totalRecords);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.totalRecords, props.pageLimit, currentPage]);

    /**
     * Helper method for creating a range of numbers
     * range(1, 5) => [1, 2, 3, 4, 5]
     */
    const range = (from, to, step = 1) => {
        let i = from;
        const range = [];

        while (i <= to) {
            range.push(i);
            i += step;
        }

        return range;
    };

    const gotoPage = (page) => {
        let currentPage = Math.max(0, Math.min(page, totalPages));
        setCurrentPage(currentPage);
    };

    const handleClick = (page, e) => {
        e.preventDefault();
        gotoPage(page);
    };

    const handleMoveLeft = (e) => {
        e.preventDefault();
        if(currentPage !== 1) {
            gotoPage(currentPage - 1);
        }
    };

    const handleMoveRight = (e) => {
        e.preventDefault();
        if(currentPage !== totalPages) {
            gotoPage(currentPage + 1);
        }
    };

    const fetchPageNumbers = () => {
        /**
         * totalNumbers: the total page numbers to show on the control
         * totalBlocks: totalNumbers + 2 to cover for the left(<) and right(>) controls
        */
        const totalNumbers = pageNeighbours * 2 + 2;
        if(totalPages > 1) {
            let pages = [];
            const leftBound = currentPage - pageNeighbours;
            const rightBound = currentPage + pageNeighbours;
            const beforeLastPage = totalPages;

            const startPage = leftBound > 1 ? leftBound : 1;
            const endPage = rightBound < beforeLastPage ? rightBound : beforeLastPage;
            pages = range(startPage, endPage);

            const pagesCount = pages.length;
            const singleSpillOffset = totalNumbers - pagesCount - 1;

            const leftSpill = startPage > 1;
            const rightSpill = endPage < beforeLastPage;

            if (leftSpill && !rightSpill) {
                const extraPages = range(startPage - singleSpillOffset, startPage - 1);
                pages = [...extraPages, ...pages];
            } else if (!leftSpill && rightSpill) {
                const extraPages = range(endPage + 1, endPage + singleSpillOffset);
                pages = [...pages, ...extraPages];
            } else if (leftSpill && rightSpill) {
                pages = [...pages];
            }
            return [LEFT_PAGE, ...pages, RIGHT_PAGE];
        } else {
            return [totalPages];
        }
    };

    useEffect(() => {
        if(totalPages > 0 && pageNeighbours && currentPage > 0) {
            setPages(fetchPageNumbers());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageNeighbours, totalPages, currentPage]);

    if (!totalRecords) {
        return(null);
    } else if (totalPages === 1) {
        return(null);
    } else if (pages) {
        return (
            <>
                <nav style={{ "display":"flex", "justifyContent":"center", "alignItems":"center" }} aria-label="Pagination">
                    <ul style={{ "padding": "0px" }} className="pagination">
                        {pages.map((page, index) => {
                            if(page === LEFT_PAGE) {
                                return (
                                    <li key={index} className="page-item">
                                        <button className={currentPage === 1 ? "page-link-disabled hidden-btn" : "page-link hidden-btn"} style={currentPage === 1 ? { "cursor": "default" } : {}} aria-label="Previous" onClick={handleMoveLeft}>
                                            <span aria-hidden="true">&laquo;</span>
                                        </button>
                                    </li>
                                );
                            } else if(page === RIGHT_PAGE) {
                                return (
                                    <li key={index} className="page-item">
                                        <button className={currentPage === totalPages ? "page-link-disabled hidden-btn" : "page-link hidden-btn"} style={currentPage === totalPages ? { "cursor": "default" } : {}} aria-label="Next" onClick={handleMoveRight}>
                                            <span aria-hidden="true">&raquo;</span>
                                        </button>
                                    </li>
                                );
                            } else {
                                return (
                                    <li key={index} className={`page-item${currentPage === page ? " active" : ""}`}>
                                        <button className="page-link hidden-btn" style={currentPage === page ? { "cursor": "default" } : {}} onClick={(e) => handleClick(page, e)}>{page}</button>
                                    </li>
                                );
                            }
                        })}
                    </ul>
                </nav>
            </>
        );
    } else {
        return(null);
    }
}

Pagination.propTypes = {
    totalRecords: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.number
    ]).isRequired,
    pageLimit: PropTypes.number,
    pageNeighbours: PropTypes.number.isRequired,
    onPageChanged: PropTypes.func,
    updateParent: PropTypes.func
};

export default Pagination;
